import type { NodePropertySchema } from '../../../types/node-properties';

const schema: NodePropertySchema = {
  nodeType: 'microsoft-sharepoint',
  version: '1.0.0',
  properties: [
    // === Authentication ===
    {
      key: 'useCredential',
      label: 'Use OAuth2 Credential',
      type: 'boolean',
      default: true,
      description: 'Use stored OAuth2 credential for user-level access to SharePoint'
    },
    {
      key: 'credentialId',
      label: 'Credential',
      type: 'credential',
      required: true,
      typeOptions: {
        credentialType: 'microsoft_sharepoint_oauth',
      },
      displayOptions: {
        show: {
          useCredential: [true]
        }
      },
      description: 'Select Microsoft SharePoint OAuth2 credential'
    },

    // === Resource Selection ===
    {
      key: 'resource',
      label: 'Resource',
      type: 'options',
      required: true,
      default: 'site',
      options: [
        { value: 'site', name: 'Site', description: 'Work with SharePoint sites' },
        { value: 'list', name: 'List', description: 'Work with SharePoint lists' },
        { value: 'listItem', name: 'List Item', description: 'Work with list items' },
        { value: 'file', name: 'File', description: 'Work with files in document libraries' },
        { value: 'folder', name: 'Folder', description: 'Work with folders in document libraries' }
      ]
    },

    // === SITE Operations ===
    {
      key: 'operation',
      label: 'Operation',
      type: 'options',
      required: true,
      default: 'get',
      displayOptions: {
        show: {
          resource: ['site']
        }
      },
      options: [
        { value: 'get', name: 'Get', description: 'Get a site by ID' },
        { value: 'getAll', name: 'Get Many', description: 'Search and list sites' }
      ]
    },

    // === LIST Operations ===
    {
      key: 'operation',
      label: 'Operation',
      type: 'options',
      required: true,
      default: 'getAll',
      displayOptions: {
        show: {
          resource: ['list']
        }
      },
      options: [
        { value: 'get', name: 'Get', description: 'Get a list by ID' },
        { value: 'getAll', name: 'Get Many', description: 'Get all lists in a site' }
      ]
    },

    // === LIST ITEM Operations ===
    {
      key: 'operation',
      label: 'Operation',
      type: 'options',
      required: true,
      default: 'getAll',
      displayOptions: {
        show: {
          resource: ['listItem']
        }
      },
      options: [
        { value: 'create', name: 'Create', description: 'Create a new list item' },
        { value: 'get', name: 'Get', description: 'Get a list item by ID' },
        { value: 'getAll', name: 'Get Many', description: 'Get all list items' },
        { value: 'update', name: 'Update', description: 'Update a list item' },
        { value: 'delete', name: 'Delete', description: 'Delete a list item' }
      ]
    },

    // === FILE Operations ===
    {
      key: 'operation',
      label: 'Operation',
      type: 'options',
      required: true,
      default: 'download',
      displayOptions: {
        show: {
          resource: ['file']
        }
      },
      options: [
        { value: 'download', name: 'Download', description: 'Download a file' },
        { value: 'update', name: 'Update', description: 'Update file name or content' },
        { value: 'upload', name: 'Upload', description: 'Upload a new file' }
      ]
    },

    // === FOLDER Operations ===
    {
      key: 'operation',
      label: 'Operation',
      type: 'options',
      required: true,
      default: 'getAll',
      displayOptions: {
        show: {
          resource: ['folder']
        }
      },
      options: [
        { value: 'get', name: 'Get', description: 'Get a folder by ID' },
        { value: 'getAll', name: 'Get Many', description: 'Get all folders in a location' }
      ]
    },

    // ===============================
    // SITE PROPERTIES
    // ===============================

    // Get Site - Site selection with expression toggle
    {
      key: 'siteId',
      label: 'Site',
      type: 'string',
      required: true,
      default: '',
      displayOptions: {
        show: {
          resource: ['site'],
          operation: ['get']
        }
      },
      description: 'Select the SharePoint site',
      modeToggle: {
        enabled: true,
        defaultMode: 'data',
        loadOptions: {
          method: 'getSites',
          cacheResults: true,
          cacheTtl: 60000,
        },
        textModeLabel: 'Expression',
        dataModeLabel: 'Select',
      },
    },

    // Get All Sites
    {
      key: 'searchQuery',
      label: 'Search Query',
      type: 'string',
      default: '',
      displayOptions: {
        show: {
          resource: ['site'],
          operation: ['getAll']
        }
      },
      description: 'Search term to filter sites. Leave empty to list all accessible sites.'
    },

    // ===============================
    // LIST PROPERTIES
    // ===============================

    // Site selection for list/listItem/file/folder operations
    {
      key: 'siteId',
      label: 'Site',
      type: 'string',
      required: true,
      default: '',
      displayOptions: {
        show: {
          resource: ['list', 'listItem', 'file', 'folder']
        }
      },
      description: 'Select the SharePoint site',
      modeToggle: {
        enabled: true,
        defaultMode: 'data',
        loadOptions: {
          method: 'getSites',
          cacheResults: true,
          cacheTtl: 60000,
        },
        textModeLabel: 'Expression',
        dataModeLabel: 'Select',
      },
    },

    // Get List - Will add dynamic list dropdown in a future update
    {
      key: 'listId',
      label: 'List ID',
      type: 'string',
      required: true,
      displayOptions: {
        show: {
          resource: ['list'],
          operation: ['get']
        }
      },
      description: 'The ID of the SharePoint list'
    },

    // ===============================
    // LIST ITEM PROPERTIES
    // ===============================

    // List ID for item operations
    {
      key: 'listId',
      label: 'List ID',
      type: 'string',
      required: true,
      displayOptions: {
        show: {
          resource: ['listItem']
        }
      },
      description: 'The ID of the SharePoint list'
    },

    // Item ID
    {
      key: 'itemId',
      label: 'Item ID',
      type: 'string',
      required: true,
      displayOptions: {
        show: {
          resource: ['listItem'],
          operation: ['get', 'update', 'delete']
        }
      },
      description: 'The ID of the list item'
    },

    // Fields (Create/Update)
    {
      key: 'fields',
      label: 'Fields (JSON)',
      type: 'text',
      default: '{}',
      displayOptions: {
        show: {
          resource: ['listItem'],
          operation: ['create', 'update']
        }
      },
      description: 'JSON object with field values. Example: {"Title": "My Item", "Status": "Active"}'
    },

    // Filter Expression
    {
      key: 'filterExpression',
      label: 'OData Filter',
      type: 'string',
      default: '',
      displayOptions: {
        show: {
          resource: ['listItem'],
          operation: ['getAll']
        }
      },
      description: 'OData filter expression. Example: fields/Status eq \'Active\''
    },

    // ===============================
    // FILE PROPERTIES
    // ===============================

    // Folder selection for file download/update operations (cascading from siteId)
    {
      key: 'folderId',
      label: 'Folder',
      type: 'string',
      default: '',
      displayOptions: {
        show: {
          resource: ['file'],
          operation: ['download', 'update']
        }
      },
      placeholder: 'Select a folder (optional)',
      description: 'Select a folder to browse files from. Leave empty to browse root.',
      modeToggle: {
        enabled: true,
        defaultMode: 'data',
        loadOptions: {
          method: 'getFolders',
          dependsOn: ['siteId'],
          cacheResults: true,
          cacheTtl: 30000,
        },
        textModeLabel: 'Expression',
        dataModeLabel: 'Select',
      },
    },

    // File selection (cascading from siteId and folderId)
    {
      key: 'fileId',
      label: 'File',
      type: 'string',
      required: true,
      default: '',
      displayOptions: {
        show: {
          resource: ['file'],
          operation: ['download', 'update']
        }
      },
      description: 'Select the file to download or update',
      modeToggle: {
        enabled: true,
        defaultMode: 'data',
        loadOptions: {
          method: 'getFiles',
          dependsOn: ['siteId', 'folderId'],
          cacheResults: true,
          cacheTtl: 30000,
        },
        textModeLabel: 'Expression',
        dataModeLabel: 'Select',
      },
    },

    // File Name (Upload)
    {
      key: 'fileName',
      label: 'File Name',
      type: 'string',
      required: true,
      displayOptions: {
        show: {
          resource: ['file'],
          operation: ['upload']
        }
      },
      description: 'Name of the file to upload (e.g., document.pdf)'
    },

    // Folder selection for upload destination (cascading from siteId)
    {
      key: 'uploadFolderId',
      label: 'Destination Folder',
      type: 'string',
      default: '',
      displayOptions: {
        show: {
          resource: ['file'],
          operation: ['upload']
        }
      },
      placeholder: 'Select a folder (optional)',
      description: 'Select the destination folder. Leave empty to upload to root.',
      modeToggle: {
        enabled: true,
        defaultMode: 'data',
        loadOptions: {
          method: 'getFolders',
          dependsOn: ['siteId'],
          cacheResults: true,
          cacheTtl: 30000,
        },
        textModeLabel: 'Expression',
        dataModeLabel: 'Select',
      },
    },

    // File Content (Upload) - supports file picker or base64 text input
    {
      key: 'contentBase64',
      label: 'File Content',
      type: 'text',
      required: true,
      customComponent: 'FileUpload',
      displayOptions: {
        show: {
          resource: ['file'],
          operation: ['upload']
        }
      },
      description: 'Upload a file or paste Base64-encoded content. Use {{ }} expressions to reference upstream node output.'
    },

    // MIME Type (auto-detected from file upload, or inferred from fileName on backend)
    {
      key: 'mimeType',
      label: 'MIME Type',
      type: 'hidden',
      default: '',
      displayOptions: {
        show: {
          resource: ['file'],
          operation: ['upload']
        }
      },
      description: 'Auto-detected MIME type of the uploaded file'
    },

    // New File Name (Update)
    {
      key: 'newFileName',
      label: 'New File Name',
      type: 'string',
      default: '',
      displayOptions: {
        show: {
          resource: ['file'],
          operation: ['update']
        }
      },
      description: 'New name for the file. Leave empty to keep current name.'
    },

    // Update Content
    {
      key: 'updateContent',
      label: 'Update Content',
      type: 'boolean',
      default: false,
      displayOptions: {
        show: {
          resource: ['file'],
          operation: ['update']
        }
      },
      description: 'Whether to update the file content'
    },

    // New Content (Update) - supports file picker or base64 text input
    {
      key: 'contentBase64',
      label: 'New Content',
      type: 'text',
      default: '',
      customComponent: 'FileUpload',
      displayOptions: {
        show: {
          resource: ['file'],
          operation: ['update'],
          updateContent: [true]
        }
      },
      description: 'Upload a file or paste Base64-encoded content. Use {{ }} expressions to reference upstream node output.'
    },

    // MIME Type for update (auto-detected from file upload)
    {
      key: 'mimeType',
      label: 'MIME Type',
      type: 'hidden',
      default: '',
      displayOptions: {
        show: {
          resource: ['file'],
          operation: ['update'],
          updateContent: [true]
        }
      },
      description: 'Auto-detected MIME type of the uploaded file'
    },

    // ===============================
    // FOLDER PROPERTIES
    // ===============================

    // Folder selection for get operation (cascading from siteId)
    {
      key: 'folderId',
      label: 'Folder',
      type: 'string',
      required: true,
      default: '',
      displayOptions: {
        show: {
          resource: ['folder'],
          operation: ['get']
        }
      },
      description: 'Select the folder to retrieve',
      modeToggle: {
        enabled: true,
        defaultMode: 'data',
        loadOptions: {
          method: 'getFolders',
          dependsOn: ['siteId'],
          cacheResults: true,
          cacheTtl: 30000,
        },
        textModeLabel: 'Expression',
        dataModeLabel: 'Select',
      },
    },

    // Parent Folder selection for getAll operation (cascading from siteId)
    {
      key: 'parentFolderId',
      label: 'Parent Folder',
      type: 'string',
      default: '',
      displayOptions: {
        show: {
          resource: ['folder'],
          operation: ['getAll']
        }
      },
      placeholder: 'Select parent folder (optional)',
      description: 'Select the parent folder. Leave empty to list root folders.',
      modeToggle: {
        enabled: true,
        defaultMode: 'data',
        loadOptions: {
          method: 'getFolders',
          dependsOn: ['siteId'],
          cacheResults: true,
          cacheTtl: 30000,
        },
        textModeLabel: 'Expression',
        dataModeLabel: 'Select',
      },
    },

    // ===============================
    // COMMON PROPERTIES
    // ===============================

    // Return All
    {
      key: 'returnAll',
      label: 'Return All',
      type: 'boolean',
      default: false,
      displayOptions: {
        show: {
          resource: ['site', 'list', 'listItem', 'folder'],
          operation: ['getAll']
        }
      },
      description: 'Return all results or limit the number'
    },

    // Limit
    {
      key: 'limit',
      label: 'Limit',
      type: 'number',
      default: 50,
      displayOptions: {
        show: {
          resource: ['site', 'list', 'listItem', 'folder'],
          operation: ['getAll'],
          returnAll: [false]
        }
      },
      description: 'Maximum number of results to return'
    },

    // Simplify Output
    {
      key: 'simplifyOutput',
      label: 'Simplify Output',
      type: 'boolean',
      default: true,
      displayOptions: {
        show: {
          resource: ['site', 'list', 'listItem', 'folder'],
          operation: ['get', 'getAll']
        }
      },
      description: 'Return a simplified version of the response'
    },

    // === DOWNLOAD OPTIONS ===
    {
      key: 'decodeTextContent',
      label: 'Decode Text Content',
      type: 'boolean',
      default: false,
      description: 'Automatically decode text files (CSV, JSON, TXT, XML) to readable text. When enabled, returns "content" as text in addition to contentBase64.',
      displayOptions: {
        show: {
          resource: ['file'],
          operation: ['download']
        }
      }
    },
    {
      key: 'parseJsonContent',
      label: 'Parse JSON Content',
      type: 'boolean',
      default: false,
      description: 'Automatically parse JSON files into an object/array. When enabled, returns "json" as the parsed data. This is faster than using a Code node for large JSON files.',
      displayOptions: {
        show: {
          resource: ['file'],
          operation: ['download'],
          decodeTextContent: [true]
        }
      }
    }
  ]
};

export default schema;
